import type { StoreApi } from 'zustand'
import type { createVerifyAccountApiClient } from '../infrastructure/verifyAccountApiClient'
import type { VerifyAccountStore } from './verifyAccountTypes'
import type { Logger } from '@/scopes/common/domain/types'
import type { ConfigurationClient } from '@/scopes/common/domain/types/configuration'
import type { LocationResolver } from '@/scopes/common/infrastructure/dom/location'
import type { QueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import { getPostAuthRedirectUrl } from './actions'
import { VerifyAccountRequestError } from './VerifyAccountRequestError'

interface VerifyAccountDependencies {
  apiClient: ReturnType<typeof createVerifyAccountApiClient>
  store: StoreApi<VerifyAccountStore>
  queryStringClient: QueryStringClient
  configurationClient: ConfigurationClient
  location: LocationResolver
  logger: Logger
}

export function createVerifyAccountActions({
  apiClient,
  store,
  queryStringClient,
  configurationClient,
  location,
  logger,
}: VerifyAccountDependencies) {
  async function pageLoaded() {
    const state = store.getState()
    if (state.status !== 'idle')
      return

    state.setIn('status', 'loading')
    let phase: 'initialization' | 'request' | 'redirect' = 'initialization'
    try {
      const tokens = new URLSearchParams(queryStringClient.get()).getAll('token')

      if (tokens.length !== 1 || !tokens[0].trim()) {
        store.getState().setIn('status', 'error')
        return
      }

      queryStringClient.remove('token')

      phase = 'request'
      const outcome = await apiClient.verifyAccount(tokens[0])
      if (outcome === 'verifiedAndSignedIn') {
        phase = 'redirect'
        store.getState().setIn('status', 'redirecting')
        location.replaceWith(getPostAuthRedirectUrl(configurationClient, location, 'accountVerified'))
        return
      }

      store.getState().setIn('status', outcome)
    }
    catch (error) {
      store.getState().setIn('status', 'error')
      logger.logError(new Error('Account verification failed'), {
        action: 'pageLoaded',
        phase,
        failure: error instanceof VerifyAccountRequestError ? error.failure : 'unexpectedError',
        ...(error instanceof VerifyAccountRequestError && error.httpStatus !== undefined && { httpStatus: error.httpStatus }),
      })
    }
  }

  return { pageLoaded }
}
